<template>
  <div class="min-h-screen bg-white">
    <!-- Sophisticated Product Detail Page -->
    <div v-if="product" class="animate-fade-in">
      <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <!-- Breadcrumbs -->
        <nav class="flex items-center gap-2 text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-4 md:mb-6">
          <NuxtLink to="/" class="hover:text-red-600 transition-all">Home</NuxtLink>
          <span class="w-1 h-1 rounded-full bg-slate-200"></span>
          <NuxtLink to="/shop" class="hover:text-red-600 transition-all">Catalog</NuxtLink>
          <span class="w-1 h-1 rounded-full bg-slate-200"></span>
          <NuxtLink :to="`/category/${slugify(product.category)}`" class="hover:text-red-600 transition-all">{{ product.category }}</NuxtLink>
        </nav>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          
          <!-- Product Visuals (Left Column) -->
          <div class="lg:col-span-5 space-y-6">
            <div class="relative w-full aspect-square bg-slate-50/50 rounded-2xl border border-slate-100 flex items-center justify-center overflow-hidden group mx-auto">
               <div v-if="product.offerProduct" class="absolute top-4 left-4 z-10 bg-red-600 text-white text-[9px] font-bold px-3 py-1.5 rounded uppercase tracking-wider shadow-sm">Sale</div>
               
               <NuxtPicture 
                 :src="mainImage || product.image" 
                 :alt="product.seo?.imageAltText || product.name" 
                 preload
                 format="avif,webp"
                 sizes="sm:100vw md:50vw lg:800px"
                 class="w-full h-full object-contain p-6 transition-transform duration-700 ease-out group-hover:scale-105" 
               />
            </div>

            <div v-if="allImages.length > 1" class="flex flex-wrap justify-center gap-3 md:gap-4">
               <button 
                 v-for="(img, idx) in allImages" 
                 :key="idx" 
                 @click="mainImage = img"
                 class="w-16 h-16 bg-slate-50 rounded-xl border transition-all duration-300 p-2 flex items-center justify-center overflow-hidden"
                 :class="[mainImage === img || (idx === 0 && !mainImage) ? 'border-slate-900 bg-white ring-2 ring-slate-900/5' : 'border-slate-100 opacity-60 hover:opacity-100']"
               >
                 <NuxtImg :src="img" format="webp" loading="lazy" class="w-full h-full object-contain" />
               </button>
            </div>
          </div>

          <!-- Product Interaction (Right Column) -->
          <div class="lg:col-span-7 space-y-4 md:space-y-6">
            
            <!-- Header Info -->
            <div class="space-y-4">
               <div class="flex items-center justify-between gap-4">
                 <div class="flex flex-wrap items-center gap-2 md:gap-3">
                    <div class="px-2 py-0.5 bg-slate-50 text-slate-500 text-[9px] font-bold uppercase tracking-widest rounded border border-slate-100">Certified Equipment</div>
                    <div v-if="product.brand" class="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{{ product.brand }}</div>
                 </div>
                 <div class="text-[9px] font-bold text-slate-400 uppercase tracking-widest shrink-0">ID: {{ productItemId }}</div>
               </div>
               <h1 class="text-xl md:text-2xl font-bold text-slate-900 tracking-tight leading-tight">{{ product.name }}</h1>

               <!-- ✅ Phase 2: SKU / Model Number — Visible in DOM for Google indexing -->
               <div class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                 <span v-if="product.sku" class="flex items-center gap-1 text-[10px] font-mono">
                   <span class="text-slate-400 font-sans font-bold uppercase tracking-widest">SKU:</span>
                   <span class="text-slate-700 font-bold">{{ product.sku }}</span>
                 </span>
                 <span class="flex items-center gap-1 text-[10px] font-mono">
                   <span class="text-slate-400 font-sans font-bold uppercase tracking-widest">Item:</span>
                   <span class="text-slate-600 font-bold">{{ productItemId }}</span>
                 </span>
                 <span v-if="product.brand" class="flex items-center gap-1 text-[10px]">
                   <span class="text-slate-400 font-bold uppercase tracking-widest">Brand:</span>
                   <span class="text-slate-700 font-bold">{{ product.brand }}</span>
                 </span>
               </div>
               
               <div class="flex items-center gap-3">
                  <div class="flex gap-0.5 text-amber-400">
                     <component :is="StarIcon" v-for="i in 5" :key="i" class="w-3.5 h-3.5" :class="i <= Math.floor(product.rating || 5) ? 'fill-current' : 'opacity-20'" />
                  </div>
                  <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">({{ reviewCount }} REVIEWS)</span>
               </div>
            </div>


            <!-- Short Description -->
            <div class="space-y-2 pt-2 border-t border-slate-50">
               <div class="text-[9px] font-bold text-red-600 uppercase tracking-widest">Operational Overview</div>
               <div class="relative">
                 <p class="text-sm text-slate-500 font-light leading-relaxed transition-all duration-500" :class="isDescriptionExpanded ? '' : 'line-clamp-3'">
                   {{ product.description }}
                 </p>
                 <button 
                   v-if="product.description && product.description.length > 150"
                   @click="isDescriptionExpanded = !isDescriptionExpanded" 
                   class="text-[10px] font-bold uppercase tracking-widest text-slate-900 mt-1 hover:text-red-600 transition-colors flex items-center gap-1"
                 >
                   {{ isDescriptionExpanded ? 'Show Less' : 'Read Full Description' }}
                   <component :is="ChevronRightIcon" class="w-3 h-3 transition-transform" :class="isDescriptionExpanded ? '-rotate-90' : 'rotate-90'" />
                 </button>
               </div>
            </div>

            <!-- Highlighted Buy Box (Price + Actions) -->
            <div class="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-100 shadow-sm space-y-4">
               
               <!-- Pricing -->
               <div class="flex flex-col gap-1 md:gap-2">
                  <div v-if="product.offerPrice" class="flex items-baseline gap-3 md:gap-4">
                     <span class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">QAR {{ product.offerPrice.toFixed(2) }}</span>
                     <div class="flex items-baseline gap-2">
                        <span class="text-sm font-medium text-slate-400 line-through">QAR {{ product.price.toFixed(2) }}</span>
                        <span class="px-2 py-0.5 bg-red-100 text-red-600 text-[9px] font-bold uppercase tracking-widest rounded">Saved QAR {{ (product.price - product.offerPrice).toFixed(0) }}</span>
                     </div>
                  </div>
                  <span v-else class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">QAR {{ (product.price || 0).toFixed(2) }}</span>
                  <p class="text-[9px] font-bold text-slate-400 uppercase tracking-wider leading-relaxed">Tax, installation and standard shipping included where applicable.</p>
               </div>

               <!-- Actions -->
               <div class="space-y-4 pt-6 border-t border-slate-200/60">
                   <div class="flex flex-wrap items-center gap-3">
                      <div class="flex items-center bg-white rounded-lg border border-slate-200 overflow-hidden h-10 shrink-0">
                         <button @click="quantity > 1 && quantity--" class="w-10 h-full flex items-center justify-center hover:bg-slate-50 text-slate-400 hover:text-slate-900 transition-all font-bold">&minus;</button>
                         <span class="w-10 text-center font-bold text-slate-900 text-xs">{{ quantity }}</span>
                         <button @click="quantity++" class="w-10 h-full flex items-center justify-center hover:bg-slate-50 text-slate-400 hover:text-slate-900 transition-all font-bold">&plus;</button>
                      </div>
                      <button @click="addToCart" class="flex-1 md:flex-none md:px-12 bg-slate-950 text-white rounded-lg h-10 flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-[9px] transition-all hover:bg-slate-800 active:scale-95 shadow-md">
                         <component :is="ShoppingCartIcon" class="w-3.5 h-3.5" />
                         Add to Cart
                      </button>
                   </div>
                   
                   <div class="flex flex-wrap gap-3">
                      <a :href="whatsappUrl" target="_blank" class="flex-1 md:flex-none h-10 px-6 rounded-lg border border-[#25D366] bg-white text-[#25D366] hover:bg-[#25D366] hover:text-white flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-[9px] transition-all shadow-sm">
                         <component :is="MessageCircleIcon" class="w-4 h-4" />
                         WhatsApp
                      </a>
                      <button class="flex-1 md:flex-none h-10 px-6 rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-red-500 hover:text-red-500 flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-[9px] transition-all shadow-sm">
                         <component :is="HeartIcon" class="w-4 h-4" />
                         Wishlist
                      </button>
                      <button @click="showEnquiryModal = true" class="flex-1 md:flex-none h-10 px-6 rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-blue-500 hover:text-blue-500 flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-[9px] transition-all shadow-sm">
                         <component :is="MessageCircleIcon" class="w-4 h-4" />
                         Enquire Now
                      </button>
                   </div>
               </div>
            </div>


          </div>
        </div>
      </div>

      <!-- Advanced Specifications -->
      <section class="bg-white py-6 md:py-8 mt-4 border-t border-slate-50">
         <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="grid lg:grid-cols-12 gap-8 md:gap-12 items-start">
               <!-- Left Side: The 3 Cards -->
               <div class="lg:col-span-6 space-y-4">
                  <div class="space-y-2 mb-6">
                     <h2 class="text-xl font-bold text-slate-900 tracking-tight">Purchase Confidence</h2>
                     <p class="text-sm text-slate-500 font-light leading-relaxed">Guaranteed service and verified authenticity</p>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                     <!-- Card 1 -->
                     <div class="p-4 rounded-xl bg-slate-50/50 border border-slate-100 flex flex-col items-center justify-center text-center gap-3">
                        <div class="w-10 h-10 bg-white rounded-full flex items-center justify-center text-slate-400 shadow-sm shrink-0">
                           <component :is="TruckIcon" class="w-4 h-4" />
                        </div>
                        <div>
                           <div class="text-[7px] font-bold text-slate-400 uppercase tracking-widest mb-1">Global Logistics</div>
                           <div class="text-[9px] font-bold text-slate-900">Standard Delivery</div>
                        </div>
                     </div>
                     
                     <!-- Card 2 -->
                     <div class="p-4 rounded-xl bg-slate-50/50 border border-slate-100 flex flex-col items-center justify-center text-center gap-3">
                        <div class="w-10 h-10 bg-white rounded-full flex items-center justify-center text-slate-400 shadow-sm shrink-0">
                           <component :is="ShieldCheckIcon" class="w-4 h-4" />
                        </div>
                        <div>
                           <div class="text-[7px] font-bold text-slate-400 uppercase tracking-widest mb-1">Factory Sealed</div>
                           <div class="text-[9px] font-bold text-slate-900">Certified Material</div>
                        </div>
                     </div>

                     <!-- Card 3 -->
                     <div class="p-4 rounded-xl bg-blue-50/30 border border-blue-100 flex flex-col items-center justify-center text-center gap-3">
                        <div class="flex -space-x-2 shrink-0">
                           <div v-for="i in 3" :key="i" class="w-8 h-8 rounded-full border-2 border-white bg-slate-200 overflow-hidden shadow-sm">
                              <img :src="`https://i.pravatar.cc/100?u=${i}`" />
                           </div>
                        </div>
                        <div>
                           <div class="text-[7px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                              <ClientOnly>
                               <span class="text-slate-900">{{ Math.floor(Math.random() * 50) + 12 }} Verified Pros</span> 
                              </ClientOnly>
                           </div>
                           <div class="text-[8px] font-bold text-slate-500 uppercase tracking-widest">Bought this unit</div>
                        </div>
                     </div>
                  </div>
               </div>
               
               <!-- Right Side: Technical Specifications Table -->
               <div class="lg:col-span-6 space-y-6">
                  <div class="space-y-2">
                     <h2 class="text-xl font-bold text-slate-900 tracking-tight">Technical Specifications</h2>
                     <p class="text-sm text-slate-500 font-light leading-relaxed">{{ productItemId }} Compliance & Product Details</p>
                  </div>
                  
                  <!-- Server-Side HTML Specifications Table for Google & Crawlers -->
                  <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <table class="w-full text-left text-sm text-slate-700 border-collapse">
                      <caption class="sr-only">Detailed Specifications for {{ product.name }}</caption>
                      <tbody class="divide-y divide-slate-100">
                        <tr class="hover:bg-slate-50/70 transition-colors">
                          <th scope="row" class="py-3 px-4 text-xs font-bold uppercase tracking-wider text-slate-400 bg-slate-50/60 w-1/3">Brand</th>
                          <td class="py-3 px-4 font-semibold text-slate-900">{{ product.brand || 'Alarm Supplies Qatar' }}</td>
                        </tr>
                        <tr class="hover:bg-slate-50/70 transition-colors">
                          <th scope="row" class="py-3 px-4 text-xs font-bold uppercase tracking-wider text-slate-400 bg-slate-50/60">Model / SKU</th>
                          <td class="py-3 px-4 font-mono font-bold text-slate-900">{{ product.sku || 'N/A' }}</td>
                        </tr>
                        <tr class="hover:bg-slate-50/70 transition-colors">
                          <th scope="row" class="py-3 px-4 text-xs font-bold uppercase tracking-wider text-slate-400 bg-slate-50/60">Product ID</th>
                          <td class="py-3 px-4 font-mono font-bold text-slate-900">{{ productItemId }}</td>
                        </tr>
                        <tr class="hover:bg-slate-50/70 transition-colors">
                          <th scope="row" class="py-3 px-4 text-xs font-bold uppercase tracking-wider text-slate-400 bg-slate-50/60">Category</th>
                          <td class="py-3 px-4 font-medium text-slate-900">{{ product.category }}</td>
                        </tr>
                        <tr class="hover:bg-slate-50/70 transition-colors">
                          <th scope="row" class="py-3 px-4 text-xs font-bold uppercase tracking-wider text-slate-400 bg-slate-50/60">Availability</th>
                          <td class="py-3 px-4">
                            <span :class="isProductInStock ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-rose-700 bg-rose-50 border-rose-200'" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border">
                              {{ isProductInStock ? 'In Stock' : 'Out of Stock' }}
                            </span>
                          </td>
                        </tr>
                        <tr v-for="(val, key) in (product.technicalSpecs || {})" :key="key" class="hover:bg-slate-50/70 transition-colors">
                          <th scope="row" class="py-3 px-4 text-xs font-bold uppercase tracking-wider text-slate-400 bg-slate-50/60">{{ key }}</th>
                          <td class="py-3 px-4 text-slate-800">{{ val }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <!-- Supporting Documents -->
                  <div v-if="product.documents && product.documents.length > 0" class="mt-6 pt-6 border-t border-slate-100 space-y-3">
                     <h3 class="text-[10px] font-bold text-slate-900 uppercase tracking-widest">Supporting Documents</h3>
                     <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <a v-for="(doc, idx) in product.documents" :key="idx" :href="doc.url" target="_blank" class="p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-red-200 hover:bg-red-50 flex items-center gap-3 transition-all group">
                           <div class="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center text-slate-400 shrink-0 group-hover:text-red-600 transition-colors">
                              <component :is="FileTextIcon" class="w-4 h-4" />
                           </div>
                           <span class="text-[10px] font-bold text-slate-700 leading-tight flex-1 line-clamp-2 group-hover:text-red-600 transition-colors">{{ doc.name || 'Document ' + (idx + 1) }}</span>
                        </a>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </section>

      <!-- AI OVERVIEW OPTIMIZED SECTION -->
      <section v-if="product.description" class="py-6 md:py-8 bg-white border-t border-slate-50">
         <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="ai-direct-answer bg-slate-50 border-l-4 border-slate-900 p-6 md:p-8 rounded-r-lg shadow-sm" aria-label="AI Summary">
               <h2 class="text-sm md:text-base font-bold uppercase tracking-widest text-slate-900 mb-4">Why Choose {{ product.seo?.primaryKeyword || displayProductName }}?</h2>
               <p class="text-base md:text-lg text-slate-700 leading-relaxed font-medium">
                  The <strong class="text-slate-900">{{ product.seo?.primaryKeyword || displayProductName }}</strong> is the optimal choice for properties in Qatar due to its compliance with Civil Defense regulations and reliable performance in extreme environments.
               </p>
               <ul v-if="product.features && product.features.length" class="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  <li v-for="(feature, idx) in product.features.slice(0, 6)" :key="idx" class="text-sm text-slate-600 flex items-start gap-3">
                     <span class="w-2 h-2 mt-1.5 rounded-full bg-red-500 shrink-0"></span> <span class="leading-relaxed">{{ feature }}</span>
                  </li>
               </ul>
            </div>
         </div>
      </section>

      <!-- Recommended Section -->
      <section v-if="suggestedProducts.length > 0" class="py-6 md:py-8 bg-white border-t border-slate-50">
         <div class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="flex items-end justify-between mb-6 gap-4">
               <div class="space-y-1 text-left flex-1">
                  <div class="text-slate-400 font-bold uppercase text-[9px] tracking-widest">Recommended</div>
                  <h2 class="text-xl md:text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">Related Inventory</h2>
               </div>
               <NuxtLink to="/shop" class="text-[9px] font-bold uppercase tracking-widest text-slate-900 hover:text-red-600 transition-colors whitespace-nowrap shrink-0">
                  View Catalog
               </NuxtLink>
            </div>
            
            <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-4">
               <div v-for="p in suggestedProducts" :key="p.id" class="flex flex-col bg-white rounded-xl border border-slate-100 p-2 md:p-3 transition-all hover:border-slate-300">
                  <div @click="navigateTo(`/product/${p.slug || p.id}`)" class="relative aspect-square rounded-lg bg-slate-50 overflow-hidden p-4 flex items-center justify-center cursor-pointer">
                     <NuxtImg :src="p.image" :alt="p.name" loading="lazy" format="webp" class="max-w-full max-h-full object-contain" />
                  </div>
                  <div class="pt-3 pb-1 space-y-2 flex-1 flex flex-col px-1">
                     <div class="text-[7px] md:text-[8px] font-bold text-red-600 uppercase tracking-widest">{{ p.category }}</div>
                     <h3 class="text-[11px] md:text-sm font-bold text-slate-800 line-clamp-2 leading-tight flex-1" @click="navigateTo(`/product/${p.slug}`)">{{ p.name }}</h3>
                     <div class="flex items-center justify-between pt-2 border-t border-slate-50">
                        <span class="text-xs md:text-base font-bold text-slate-900">QAR {{ (p.price || 0).toFixed(2) }}</span>
                        <div @click="navigateTo(`/product/${p.slug}`)" class="w-6 h-6 rounded bg-slate-900 text-white flex items-center justify-center cursor-pointer">
                           <component :is="ChevronRightIcon" class="w-3 h-3" />
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </section>
    </div>

    <!-- Error/Loading -->
    <div v-else class="flex flex-col items-center justify-center py-40 container mx-auto text-center px-6">
       <div v-if="loading" class="animate-pulse flex flex-col items-center">
          <div class="w-12 h-12 rounded-full border-2 border-slate-100 border-t-slate-900 animate-spin mb-4"></div>
          <span class="text-[10px] font-bold uppercase tracking-widest text-slate-400">Syncing...</span>
       </div>
       <div v-else class="space-y-6">
          <div class="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto text-slate-300">
             <component :is="PackageSearchIcon" class="w-8 h-8" />
          </div>
          <div class="space-y-1">
             <h2 class="text-xl font-bold text-slate-900">Unit Not Found</h2>
             <p class="text-sm text-slate-400 font-light max-w-xs mx-auto">This inventory item may have been relocated or discontinued.</p>
          </div>
          <NuxtLink to="/shop" class="inline-flex h-11 px-8 items-center justify-center bg-slate-900 text-white rounded font-bold uppercase tracking-widest text-[10px]">
             Back to Catalog
          </NuxtLink>
       </div>
    </div>
    
    <LeadsProductEnquiryModal v-if="product" v-model="showEnquiryModal" :productName="product.name" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watchEffect } from 'vue'
import { useRoute, useRouter, useSeoMeta, useHead, useAsyncData } from '#app'
import { slugify } from '~/utils/slugify'
import { 
  Star as StarIcon, 
  Heart as HeartIcon, 
  Truck as TruckIcon, 
  RotateCcw as RotateCcwIcon,
  ShieldCheck as ShieldCheckIcon,
  Activity as ActivityIcon,
  Flame as FlameIcon,
  Zap as ZapIcon,
  Award as AwardIcon,
  ChevronRight as ChevronRightIcon,
  MessageCircle as MessageCircleIcon,
  FileText as FileTextIcon,
  ShoppingCart as ShoppingCartIcon,
  PackageSearch as PackageSearchIcon,
  ArrowRight as ArrowRightIcon,
  Box as BoxIcon,
  BadgeCheck as BadgeCheckIcon,
  Clock as ClockIcon
} from 'lucide-vue-next'
import { useProductStore, type Product } from '~/stores/products'
import { useCartStore } from '~/stores/cart'
import { useTrackingStore } from '~/stores/tracking'

const route = useRoute()
const router = useRouter()
const productStore = useProductStore()
const cartStore = useCartStore()
const trackingStore = useTrackingStore()

const slug = route.params.slug as string

// Fetch product data during SSR
const { data: product } = await useAsyncData<Product>(`product-${slug}`, () => 
  $fetch(`/api/products/${slug}`)
)

if (!product.value) {
  throw createError({ statusCode: 404, statusMessage: 'Product Not Found', fatal: true })
}

const productItemId = computed(() => {
  const p = product.value as any
  if (!p) return ''
  return p.itemId || (p.id ? `ALM-${String(p.id).slice(-6).toUpperCase()}` : '')
})

const isProductInStock = computed(() => {
  const p = product.value
  if (!p) return false
  if (p.stock !== undefined) return p.stock > 0
  return (p as any).inStock ?? (p.status !== 'Out of Stock')
})

const sanitizeSlug = (s: string) => {
  if (!s) return ''
  return s
    .toLowerCase()
    .trim()
    .replace(/[:*+()]/g, '-')
    .replace(/[^\w-]/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const canonicalSlug = (product.value as any)?.cleanSlug || product.value?.seo?.slug || (product.value?.slug ? sanitizeSlug(product.value.slug) : String(product.value?.id))

// 301 Redirect for old URLs, raw IDs, or SKU access to canonical SEO slug
if (canonicalSlug && slug !== canonicalSlug) {
  navigateTo(`/product/${canonicalSlug}`, { redirectCode: 301 })
}

const productUrl = `https://alarmsupplies.qa/product/${canonicalSlug || slug}`
const pageTitle = `${product.value.name} (${product.value.sku || 'N/A'}) - ${product.value.brand || 'Alarm Supplies'} | Alarm Supplies Qatar`
const pageDesc = `Buy ${product.value.name} in Qatar. SKU: ${product.value.sku || 'N/A'}, Product ID: ${productItemId.value}, Brand: ${product.value.brand || 'Alarm Supplies Qatar'}. Genuine fire safety equipment with fast delivery in Doha.`

// Canonical & Meta Override (key: 'canonical' is mandatory to override nuxt.config)
useHead({
  title: pageTitle,
  link: [
    { rel: 'canonical', href: productUrl, key: 'canonical' }
  ],
  meta: [
    { name: 'description', content: pageDesc, key: 'description' },
    { property: 'og:title', content: pageTitle, key: 'og:title' },
    { property: 'og:description', content: pageDesc, key: 'og:description' },
    { property: 'og:url', content: productUrl, key: 'og:url' }
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.value.name,
        "image": (product.value as any).images || [product.value.image || "https://alarmsupplies.qa/images/logo.png"],
        "description": pageDesc,
        "sku": product.value.sku || productItemId.value,
        "mpn": product.value.sku || productItemId.value,
        "productID": productItemId.value,
        "brand": {
          "@type": "Brand",
          "name": product.value.brand || "Alarm Supplies Qatar"
        },
        "offers": {
          "@type": "Offer",
          "priceCurrency": "QAR",
          "price": product.value.offerPrice || product.value.price || 0,
          "availability": isProductInStock.value ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "itemCondition": "https://schema.org/NewCondition",
          "url": productUrl
        }
      })
    }
  ]
})

// Format raw SKU titles into beautiful human readable titles
const displayProductName = computed(() => {
  if (!product.value?.name) return ''
  let name = product.value.name.replace(/BRAND:\s*[A-Z0-9]+/i, '')
  return name.toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase()).trim()
})

const quantity = ref(1)
const loading = ref(false)
const isDescriptionExpanded = ref(false)
const showEnquiryModal = ref(false)

const mainImage = ref('')
const allImages = computed(() => {
  if (!product.value) return []
  return [product.value.image, ...(product.value.subImages || [])]
})

const trustMarkers = [
  { title: 'Standard Delivery', subtitle: 'Global Logistics', icon: TruckIcon },
  { title: 'Certified Material', subtitle: 'Factory Sealed', icon: BadgeCheckIcon },
  { title: 'Engineer Support', subtitle: '24/7 Hotline', icon: ClockIcon }
]

watchEffect(() => {
    if (product.value && !mainImage.value) {
        mainImage.value = product.value.image
    }
})

const reviewCount = computed(() => {
  if (!product.value) return 50
  let hash = 0
  const idStr = String(product.value.id)
  for (let i = 0; i < idStr.length; i++) {
    hash = idStr.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash % 200) + 50
})

const suggestedProducts = computed(() => {
  if (!product.value) return []
  return [...productStore.products]
    .filter(p => p.id !== product.value?.id)
    .sort((a, b) => String(a.id).localeCompare(String(b.id)))
    .slice(0, 12)
})

onMounted(async () => {
  if (productStore.products.length === 0) {
    loading.value = true
    await productStore.fetchProducts()
    loading.value = false
  }
  if (product.value) {
    trackingStore.addScore(5, 'Product View: ' + product.value.name)
    trackingStore.addRecentlyViewed(product.value.name)
  }
})

const addToCart = () => {
  if (product.value) {
    for (let i = 0; i < quantity.value; i++) {
        cartStore.addItem(product.value as any)
    }
    trackingStore.addScore(15, 'Add to Cart: ' + product.value.name)
  }
}

const whatsappUrl = computed(() => {
  if (!product.value) return '#'
  const currentPrice = product.value.offerPrice || product.value.price
  const message = `Hello, I'm interested in: ${product.value.name} (QAR ${currentPrice.toFixed(2)})`
  return `https://wa.me/97430812223?text=${encodeURIComponent(message)}`
})
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
